/**
 * useTemplateManagement
 *
 * State management hook for the Templates tab in the Data Management Hub.
 * Handles template CRUD, folder organization, search/filter, sort, and preview.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import {
  queryTemplateApi,
  templateFolderApi,
  QueryTemplate,
  CreateQueryTemplateDTO,
  TemplateFolderRecord,
} from '@/services/api/queryTemplateApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FolderTreeNode {
  /** Leaf segment name, e.g. "Pressure" */
  name: string;
  /** Full slash-separated path, e.g. "Barn/Pressure" */
  path: string;
  /** DB record id (if exists in TemplateFolder) */
  folderId?: string;
  /** Child folders */
  children: FolderTreeNode[];
  /** Templates in this folder + all descendants */
  totalCount: number;
}

export interface UseTemplateManagementReturn {
  // Data
  templates: QueryTemplate[];
  folderTree: FolderTreeNode[];
  total: number;

  // Filters
  selectedFolder: string | null;
  searchQuery: string;
  sortBy: 'name' | 'createdAt' | 'usageCount';
  sortOrder: 'asc' | 'desc';

  // Tag filtering
  allTags: string[];
  selectedTags: string[];
  toggleTag: (tag: string) => void;
  setSelectedTags: (tags: string[]) => void;

  // Loading states
  isLoading: boolean;
  isOperating: boolean;

  // Folder tree state
  expandedPaths: Set<string>;
  toggleFolderExpand: (path: string) => void;

  // Filter setters
  setSelectedFolder: (folder: string | null) => void;
  setSearchQuery: (query: string) => void;
  setSortBy: (sort: 'name' | 'createdAt' | 'usageCount') => void;
  setSortOrder: (order: 'asc' | 'desc') => void;

  // Template CRUD
  fetchTemplates: () => Promise<void>;
  renameTemplate: (id: string, newName: string) => Promise<void>;
  updateTemplate: (id: string, updates: Partial<CreateQueryTemplateDTO>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  duplicateTemplate: (template: QueryTemplate) => Promise<void>;
  moveToFolder: (id: string, folder: string | undefined) => Promise<void>;

  // Folder operations
  createFolder: (name: string) => Promise<void>;
  createSubfolder: (parentPath: string, childName: string) => Promise<void>;
  renameFolder: (folderPath: string, newLeafName: string) => Promise<void>;
  deleteFolder: (folderPath: string) => Promise<void>;

  // Preview
  expandedTemplateId: string | null;
  previewData: Record<string, unknown>[] | null;
  isPreviewLoading: boolean;
  togglePreview: (templateId: string) => void;

  // Edit
  editingTemplateId: string | null;
  setEditingTemplateId: (id: string | null) => void;

  // Inline rename
  renamingTemplateId: string | null;
  setRenamingTemplateId: (id: string | null) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a tree from flat folder paths.
 * Ensures intermediate nodes exist even if no DB record matches.
 */
function buildFolderTree(
  dbFolders: TemplateFolderRecord[],
  templates: QueryTemplate[]
): FolderTreeNode[] {
  const root: FolderTreeNode[] = [];
  const nodeMap = new Map<string, FolderTreeNode>();

  // Collect all unique folder paths from both sources
  const allPaths = new Set<string>();
  for (const f of dbFolders) allPaths.add(f.name);
  for (const t of templates) {
    if (t.folder) allPaths.add(t.folder);
  }

  // Sort so parents come before children
  const sortedPaths = Array.from(allPaths).sort();

  for (const path of sortedPaths) {
    const segments = path.split('/');
    const leafName = segments[segments.length - 1];
    const dbRecord = dbFolders.find((f) => f.name === path);

    const node: FolderTreeNode = {
      name: leafName,
      path,
      folderId: dbRecord?.id,
      children: [],
      totalCount: 0,
    };
    nodeMap.set(path, node);

    if (segments.length === 1) {
      root.push(node);
    } else {
      const parentPath = segments.slice(0, -1).join('/');
      let parent = nodeMap.get(parentPath);
      if (!parent) {
        // Create implicit parent node
        const parentSegments = parentPath.split('/');
        parent = {
          name: parentSegments[parentSegments.length - 1],
          path: parentPath,
          folderId: dbFolders.find((f) => f.name === parentPath)?.id,
          children: [],
          totalCount: 0,
        };
        nodeMap.set(parentPath, parent);
        // Attach implicit parent to its own parent or root
        if (parentSegments.length === 1) {
          root.push(parent);
        } else {
          const grandparentPath = parentSegments.slice(0, -1).join('/');
          const grandparent = nodeMap.get(grandparentPath);
          if (grandparent) grandparent.children.push(parent);
          else root.push(parent);
        }
      }
      parent.children.push(node);
    }
  }

  // Count templates per exact folder
  const directCounts = new Map<string, number>();
  for (const t of templates) {
    if (t.folder) {
      directCounts.set(t.folder, (directCounts.get(t.folder) || 0) + 1);
    }
  }

  // Compute totalCount recursively (direct + all descendants)
  function computeTotal(node: FolderTreeNode): number {
    const direct = directCounts.get(node.path) || 0;
    const childTotal = node.children.reduce((sum, c) => sum + computeTotal(c), 0);
    node.totalCount = direct + childTotal;
    return node.totalCount;
  }
  root.forEach(computeTotal);

  // Sort children alphabetically
  function sortChildren(nodes: FolderTreeNode[]) {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach((n) => sortChildren(n.children));
  }
  sortChildren(root);

  return root;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTemplateManagement(orgId?: string | null): UseTemplateManagementReturn {
  const [templates, setTemplates] = useState<QueryTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [dbFolders, setDbFolders] = useState<TemplateFolderRecord[]>([]);

  // Filters
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'createdAt' | 'usageCount'>('usageCount');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Loading
  const [isLoading, setIsLoading] = useState(false);
  const [isOperating, setIsOperating] = useState(false);

  // Preview
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<Record<string, unknown>[] | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // Edit / rename
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [renamingTemplateId, setRenamingTemplateId] = useState<string | null>(null);

  // Track if initial fetch has been done
  const hasFetched = useRef(false);

  // Folder tree expand/collapse state
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  const toggleFolderExpand = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // Build folder tree from DB records + template folder values
  const folderTree = useMemo(
    () => buildFolderTree(dbFolders, templates),
    [dbFolders, templates]
  );

  // Collect all unique tags from loaded templates
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const t of templates) {
      t.tags?.forEach((tag) => tagSet.add(tag));
    }
    return Array.from(tagSet).sort();
  }, [templates]);

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }, []);

  // --- Fetch ---
  // Build per-request org header config for cross-org access in personal space
  const orgAxiosConfig = orgId
    ? { headers: { 'X-Organization-Id': orgId } }
    : undefined;

  const fetchFolders = useCallback(async () => {
    try {
      const result = await templateFolderApi.list(orgAxiosConfig);
      setDbFolders(result);
    } catch {
      // Silent — folders are supplementary
    }
  }, [orgId]);

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const [result] = await Promise.all([
        queryTemplateApi.list({
          search: searchQuery || undefined,
          sortBy,
          sortOrder,
          limit: 100,
        }, orgAxiosConfig),
        fetchFolders(),
      ]);
      setTemplates(result.templates);
      setTotal(result.total);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, sortBy, sortOrder, fetchFolders, orgId]);

  // Initial fetch on mount
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchTemplates();
    }
  }, [fetchTemplates]);

  // Re-fetch when org context changes (cross-org template browsing)
  const prevOrgId = useRef(orgId);
  useEffect(() => {
    if (prevOrgId.current !== orgId) {
      prevOrgId.current = orgId;
      fetchTemplates();
    }
  }, [orgId, fetchTemplates]);

  // Debounced re-fetch when search changes
  useEffect(() => {
    if (!hasFetched.current) return;
    const timer = setTimeout(() => {
      fetchTemplates();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, sortBy, sortOrder, fetchTemplates]);

  // --- Template CRUD ---
  const renameTemplate = useCallback(
    async (id: string, newName: string) => {
      setIsOperating(true);
      try {
        await queryTemplateApi.update(id, { name: newName });
        toast.success('Template renamed');
        setRenamingTemplateId(null);
        await fetchTemplates();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to rename template');
      } finally {
        setIsOperating(false);
      }
    },
    [fetchTemplates]
  );

  const updateTemplate = useCallback(
    async (id: string, updates: Partial<CreateQueryTemplateDTO>) => {
      setIsOperating(true);
      try {
        await queryTemplateApi.update(id, updates);
        toast.success('Template updated');
        setEditingTemplateId(null);
        await fetchTemplates();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update template');
      } finally {
        setIsOperating(false);
      }
    },
    [fetchTemplates]
  );

  const deleteTemplate = useCallback(
    async (id: string) => {
      setIsOperating(true);
      try {
        await queryTemplateApi.delete(id);
        toast.success('Template deleted');
        if (expandedTemplateId === id) {
          setExpandedTemplateId(null);
          setPreviewData(null);
        }
        if (editingTemplateId === id) setEditingTemplateId(null);
        if (renamingTemplateId === id) setRenamingTemplateId(null);
        await fetchTemplates();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete template');
      } finally {
        setIsOperating(false);
      }
    },
    [fetchTemplates, expandedTemplateId, editingTemplateId, renamingTemplateId]
  );

  const duplicateTemplate = useCallback(
    async (template: QueryTemplate) => {
      setIsOperating(true);
      try {
        await queryTemplateApi.create({
          name: `${template.name} (Copy)`,
          description: template.description,
          templateType: template.templateType,
          query: template.query,
          compositeQueryDefinition: template.compositeQueryDefinition,
          variables: template.variables,
          tags: template.tags,
          folder: template.folder,
        });
        toast.success('Template duplicated');
        await fetchTemplates();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to duplicate template');
      } finally {
        setIsOperating(false);
      }
    },
    [fetchTemplates]
  );

  const moveToFolder = useCallback(
    async (id: string, folder: string | undefined) => {
      setIsOperating(true);
      try {
        await queryTemplateApi.update(id, { folder } as Partial<CreateQueryTemplateDTO>);
        toast.success(folder ? `Moved to "${folder}"` : 'Moved to Uncategorized');
        await fetchTemplates();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to move template');
      } finally {
        setIsOperating(false);
      }
    },
    [fetchTemplates]
  );

  // --- Folder operations (API-backed) ---
  const createFolder = useCallback(
    async (name: string) => {
      setIsOperating(true);
      try {
        await templateFolderApi.create(name);
        toast.success(`Folder "${name}" created`);
        await fetchFolders();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create folder');
      } finally {
        setIsOperating(false);
      }
    },
    [fetchFolders]
  );

  const renameFolder = useCallback(
    async (folderPath: string, newLeafName: string) => {
      const folderRecord = dbFolders.find((f) => f.name === folderPath);
      if (!folderRecord) {
        toast.error('Folder not found');
        return;
      }

      // Build new full path by replacing the leaf segment
      const segments = folderPath.split('/');
      segments[segments.length - 1] = newLeafName;
      const newPath = segments.join('/');

      setIsOperating(true);
      try {
        await templateFolderApi.rename(folderRecord.id, newPath);
        // Update selected folder if it was this path or a child
        if (selectedFolder === folderPath) {
          setSelectedFolder(newPath);
        } else if (selectedFolder?.startsWith(folderPath + '/')) {
          setSelectedFolder(newPath + selectedFolder.substring(folderPath.length));
        }
        toast.success(`Folder renamed to "${newLeafName}"`);
        await fetchTemplates();
      } catch (err) {
        toast.error('Failed to rename folder');
      } finally {
        setIsOperating(false);
      }
    },
    [dbFolders, selectedFolder, fetchTemplates]
  );

  const deleteFolder = useCallback(
    async (folderPath: string) => {
      const folderRecord = dbFolders.find((f) => f.name === folderPath);
      if (!folderRecord) {
        toast.error('Folder not found');
        return;
      }

      setIsOperating(true);
      try {
        await templateFolderApi.remove(folderRecord.id);
        // Clear selection if it was this folder or a child
        if (selectedFolder === folderPath || selectedFolder?.startsWith(folderPath + '/')) {
          setSelectedFolder(null);
        }
        toast.success('Folder deleted');
        await fetchTemplates();
      } catch (err) {
        toast.error('Failed to delete folder');
      } finally {
        setIsOperating(false);
      }
    },
    [dbFolders, selectedFolder, fetchTemplates]
  );

  const createSubfolder = useCallback(
    async (parentPath: string, childName: string) => {
      const fullPath = `${parentPath}/${childName}`;
      if (fullPath.split('/').length > 3) {
        toast.error('Maximum folder depth is 3 levels');
        return;
      }
      setIsOperating(true);
      try {
        await templateFolderApi.create(fullPath);
        toast.success(`Subfolder "${childName}" created`);
        // Auto-expand the parent
        setExpandedPaths((prev) => new Set([...prev, parentPath]));
        await fetchFolders();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create subfolder');
      } finally {
        setIsOperating(false);
      }
    },
    [fetchFolders]
  );

  // --- Preview ---
  const togglePreview = useCallback(
    (templateId: string) => {
      if (expandedTemplateId === templateId) {
        setExpandedTemplateId(null);
        setPreviewData(null);
        return;
      }

      setExpandedTemplateId(templateId);
      setPreviewData(null);
      setIsPreviewLoading(true);

      queryTemplateApi
        .execute(templateId, {})
        .then((result) => {
          setPreviewData(result.data.slice(0, 5));
        })
        .catch(() => {
          setPreviewData(null);
        })
        .finally(() => {
          setIsPreviewLoading(false);
        });
    },
    [expandedTemplateId]
  );

  return {
    templates,
    folderTree,
    total,
    selectedFolder,
    searchQuery,
    sortBy,
    sortOrder,
    allTags,
    selectedTags,
    toggleTag,
    setSelectedTags,
    isLoading,
    isOperating,
    expandedPaths,
    toggleFolderExpand,
    setSelectedFolder,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    fetchTemplates,
    renameTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    moveToFolder,
    createFolder,
    createSubfolder,
    renameFolder,
    deleteFolder,
    expandedTemplateId,
    previewData,
    isPreviewLoading,
    togglePreview,
    editingTemplateId,
    setEditingTemplateId,
    renamingTemplateId,
    setRenamingTemplateId,
  };
}
